/**
 * EventColorEditor component for editing event color in the sidebar.
 * Uses debounced updates (300ms) to persist color changes to the backend.
 */

import { Skeleton } from "@mui/material";
import chroma from "chroma-js";
import * as React from "react";

import { EventRecord } from "@/shared/domain/events";
import { ErrorOutlineIcon } from "@/shared/foundation/icons/mui";
import { useDebouncedCallback } from "@/shared/hooks";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";

import { RobotoTypography } from "../../RobotoTypography";
import { colorForAnnotation } from "../colorScale";
import {
  useUpdateWorkspaceEvent,
  useWorkspaceEvent,
} from "../WorkspaceCtx/useWorkspaceEvents";

import styles from "./EventColor.module.css";

interface EventColorEditorProps {
  eventId: EventRecord["event_id"];
}

/**
 * Converts an RGBA color string to hex format for the color picker.
 * The native HTML color input only accepts hex format (#rrggbb).
 */
function rgbaToHex(rgbaColor: string): string {
  try {
    return chroma(rgbaColor).hex();
  } catch {
    return "#3b82f6"; // Fallback blue
  }
}

export function EventColorEditor({ eventId }: EventColorEditorProps) {
  const eventQuery = useWorkspaceEvent(eventId);
  const updateEventMutation = useUpdateWorkspaceEvent();

  // Debounce color updates to avoid excessive API calls while dragging
  const debouncedUpdateColor = useDebouncedCallback((color: string) => {
    updateEventMutation.mutate(
      {
        eventId,
        request: {
          display_options_changeset: { color },
        },
      },
      {
        onError: (error) => {
          ErrorMonitoringService.captureError(error);
        },
      },
    );
  }, 300);

  const handleColorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    debouncedUpdateColor(event.target.value);
  };

  // Reset to auto-generated color by sending null
  const handleResetColor = () => {
    updateEventMutation.mutate(
      {
        eventId,
        request: {
          display_options_changeset: { color: null },
        },
      },
      {
        onError: (error) => {
          ErrorMonitoringService.captureError(error);
        },
      },
    );
  };

  // Loading state
  if (eventQuery.isPending) {
    return (
      <section>
        <RobotoTypography component={"h3"} className={styles.title}>
          Color
        </RobotoTypography>
        <div className={styles.sectionPadding}>
          <Skeleton variant="rounded" className={styles.colorSkeleton} />
        </div>
      </section>
    );
  }

  // Error state
  if (eventQuery.isError) {
    return (
      <section>
        <RobotoTypography component={"h3"} className={styles.title}>
          Color
        </RobotoTypography>
        <div className={styles.errorContainer}>
          <ErrorOutlineIcon color="error" />
          <RobotoTypography>Failed to load event color</RobotoTypography>
        </div>
      </section>
    );
  }

  // Use custom color if set, otherwise fall back to auto-generated
  const customColor = eventQuery.data.display_options?.color;
  const autoGeneratedColor = colorForAnnotation(eventQuery.data.name, 1.0);
  const displayColor = customColor ?? rgbaToHex(autoGeneratedColor);
  return (
    <section>
      <RobotoTypography component={"h3"} className={styles.title}>
        Color
      </RobotoTypography>
      <div className={styles.colorContainer}>
        <input
          type="color"
          className={styles.colorInput}
          value={displayColor}
          onChange={handleColorChange}
          aria-label="Change event color"
        />
        <RobotoTypography className={styles.colorLabel}>
          {customColor ? "Custom" : "Auto"}
        </RobotoTypography>
        {customColor && (
          <button
            type="button"
            className={styles.resetButton}
            onClick={handleResetColor}
          >
            Reset
          </button>
        )}
      </div>
    </section>
  );
}
